import { Box, CircularProgress, DialogContent } from "@mui/material"
import { PersonAddOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useMemo, useState } from "react"

import type { SalesCustomer, SalesRequester } from "../../types"
import { formatFullName } from "../../utils/format"

import { ConfirmedHeaderSummary } from "./ConfirmedHeaderSummary"
import { NewRequestStepActions } from "./NewRequestStepActions"
import { StepSectionLabel } from "./StepSectionLabel"

import { AerosMultiSelect } from "@/components/UiKit"
import { ContactChannel } from "@/graphql/codegen/graphql"
import { useGetContacts } from "@/screens/Companies/hooks/useGetContacts"
import colors from "@/styles/colors"

interface RequesterStepProps {
  customer: SalesCustomer
  onCancel: () => void
  onContinue: (requesters: SalesRequester[]) => void
}

export const RequesterStep: FC<RequesterStepProps> = ({ customer, onCancel, onContinue }) => {
  const [selectedRequesterIds, setSelectedRequesterIds] = useState<string[]>([])

  const { contactList, contactListFetching } = useGetContacts(customer.company.companyId)
  const activeContacts = useMemo(
    () =>
      contactList.filter((contact): contact is typeof contact & { contactId: string } =>
        Boolean(contact.isActive && contact.contactId)
      ),
    [contactList]
  )
  const availableRequesters = useMemo<SalesRequester[]>(
    () =>
      activeContacts.map((contact) => ({
        contactId: contact.contactId,
        firstName: contact.firstName ?? "",
        lastName: contact.lastName ?? "",
      })),
    [activeContacts]
  )
  const requesterOptions = useMemo(
    () =>
      activeContacts.map((contact) => {
        const name = formatFullName(contact.firstName, contact.lastName)
        const email = contact.contactMethod?.find((method) => method?.contactChannel === ContactChannel.Email)?.value
        const phone = contact.contactMethod?.find((method) => method?.contactChannel === ContactChannel.Phone)?.value
        const details = [email, phone].filter(Boolean).join(" ")
        return { value: contact.contactId, label: details ? `${name} - ${details}` : name }
      }),
    [activeContacts]
  )
  const requesterNamesById = useMemo(
    () =>
      new Map(
        availableRequesters.map((requester) => [
          requester.contactId,
          formatFullName(requester.firstName, requester.lastName),
        ])
      ),
    [availableRequesters]
  )
  const canContinue = selectedRequesterIds.length > 0

  const handleContinue = () => {
    const selectedRequesters = availableRequesters.filter((requester) =>
      selectedRequesterIds.includes(requester.contactId)
    )
    onContinue(selectedRequesters)
  }

  return (
    <>
      <DialogContent className='flex flex-col gap-6 px-8 py-6'>
        <ConfirmedHeaderSummary formValues={{ customer }} />

        <Box className='flex flex-col gap-2'>
          <StepSectionLabel icon={<PersonAddOutlined />} label='Select Requester' required />

          {contactListFetching && contactList.length === 0 && (
            <Box className='flex justify-center py-2'>
              <CircularProgress size={24} />
            </Box>
          )}

          {!(contactListFetching && contactList.length === 0) && (
            <Box sx={{ "& .MuiChip-root": { backgroundColor: colors.grey[200] } }}>
              <AerosMultiSelect
                withSelectAll
                options={requesterOptions}
                value={selectedRequesterIds}
                onChange={setSelectedRequesterIds}
                getTagLabel={(option) => requesterNamesById.get(option.value) ?? String(option.label)}
                placeholder='Select requesters...'
                disabled={availableRequesters.length === 0}
                data-testid='sales.requester-step.requester-field'
              />
            </Box>
          )}
        </Box>
      </DialogContent>

      <NewRequestStepActions
        onCancel={onCancel}
        onContinue={handleContinue}
        continueDisabled={!canContinue}
        cancelTestId='sales.requester-step.cancel-button'
        continueTestId='sales.requester-step.continue-button'
      />
    </>
  )
}
